/**
 * Herkunft und NEU in der Planungszentrale (14.10.2026), reine Regeln ohne React.
 * Getestet in `__tests__/planungHerkunft.test.ts`; Doku `docs/planungszentrale.md`, Schritt 9.
 */

interface Aufgabe {
  id: string;
  status: string;
  created_at: string;
  quelle?: string | null;
  quelle_info?: string | null;
}

/**
 * Ab hier gibt es das Kennzeichen NEU. Ältere Aufgaben hat niemand als geöffnet gemerkt;
 * ohne diese Grenze stünde der ganze Bestand als neu da. Dieselbe Grenze steht in der View
 * `cockpit_aufgaben` (Migration 20261014120000).
 */
export const NEU_AB = "2026-10-14T00:00:00+02:00";

/**
 * NEU: Board-Aufgabe, offen, ab `NEU_AB` angelegt und von mir noch nie geöffnet. Wer eine
 * Aufgabe angemeldet anlegt, hat sie gesehen (Trigger `board_tasks_ersteller_gesehen`);
 * Aufgaben aus dem Meeting-Lauf sind deshalb für alle neu. Kurs-Aufgaben (`cn_`) nie.
 */
export function istNeu(aufgabe: Aufgabe, gesehen: ReadonlySet<string>): boolean {
  if (aufgabe.id.startsWith("cn_") || aufgabe.status === "done") return false;
  if (Date.parse(aufgabe.created_at) < Date.parse(NEU_AB)) return false;
  return !gesehen.has(aufgabe.id);
}

/** Auswahl im Filter „Herkunft“. Kurs-Aufgaben haben keine Herkunft und zählen als manuell. */
export const HERKUNFT_FILTER: { wert: string; label: string }[] = [
  { wert: "__all__", label: "Alle Herkünfte" },
  { wert: "meeting", label: "Aus Meeting" },
  { wert: "claude", label: "Von Claude" },
  { wert: "manuell", label: "Manuell" },
  { wert: "mail", label: "Aus Mail" },
];

export function istHerkunftFilter(wert: string | null): wert is string {
  return !!wert && HERKUNFT_FILTER.some((h) => h.wert === wert);
}

/** Passt die Aufgabe zu „Herkunft“ und „Aus Meeting …“? Ein gewähltes Meeting setzt Herkunft Meeting voraus. */
export function passtZurHerkunft(aufgabe: Aufgabe, herkunft: string, meeting: string | null): boolean {
  const quelle = aufgabe.quelle ?? "manuell";
  if (herkunft !== "__all__" && quelle !== herkunft) return false;
  if (meeting && (quelle !== "meeting" || (aufgabe.quelle_info ?? "").trim() !== meeting)) return false;
  return true;
}

/** Vorhandene Meetings für die Auswahl, das jüngste zuerst (nach der neuesten Aufgabe daraus). */
export function meetingsAusAufgaben(aufgaben: Aufgabe[]): string[] {
  const neueste = new Map<string, string>();
  for (const a of aufgaben) {
    const info = (a.quelle_info ?? "").trim();
    if (a.quelle !== "meeting" || !info) continue;
    const bisher = neueste.get(info);
    if (!bisher || a.created_at > bisher) neueste.set(info, a.created_at);
  }
  return [...neueste.entries()].sort((x, y) => y[1].localeCompare(x[1]) || x[0].localeCompare(y[0], "de")).map(([info]) => info);
}

/** Nur https-Links öffnen (die Datenbank lässt auch nur solche zu). */
export function transkriptLink(url: string | null | undefined): string | null {
  const s = (url ?? "").trim();
  return /^https:\/\/\S+$/i.test(s) ? s : null;
}

/** Zweite Zeile auf der Karte: „Aus Meeting · MK Meeting Daniel/Bianca 13.10.2026“. */
export function meetingZeile(quelle: string | null | undefined, info: string | null | undefined): string | null {
  if (quelle !== "meeting") return null;
  const zusatz = (info ?? "").trim();
  return zusatz ? `Aus Meeting · ${zusatz}` : "Aus Meeting";
}
